import { format, parseISO, isValid } from 'date-fns';
import { OrderStatus, CloudOrderState } from '../types';

// Date formatting utilities
export const formatDate = (dateString: string | undefined, formatStr: string = 'PPp'): string => {
  if (!dateString) return 'N/A';
  try {
    const date = parseISO(dateString);
    return isValid(date) ? format(date, formatStr) : 'Invalid Date';
  } catch {
    return 'Invalid Date';
  }
};

export const formatDateTime = (dateString: string | undefined): string => {
  return formatDate(dateString, 'MMM d, yyyy h:mm a');
};

export const formatTime = (dateString: string | undefined): string => {
  return formatDate(dateString, 'h:mm a');
};

export const formatDateOnly = (dateString: string | undefined): string => {
  return formatDate(dateString, 'MMM d, yyyy');
};

// Currency formatting
export const formatCurrency = (amount: number | undefined, currency: string = 'USD'): string => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
};

// Order status utilities
export const getOrderStatusText = (status: OrderStatus): string => {
  switch (status) {
    case OrderStatus.New:
      return 'New';
    case OrderStatus.Pending:
      return 'Pending';
    case OrderStatus.SentToPOS:
      return 'Sent to POS';
    case OrderStatus.Failed:
      return 'Failed';
    case OrderStatus.CancelledInPOS:
      return 'Cancelled in POS';
    case OrderStatus.CancelFailed:
      return 'Cancel Failed';
    case OrderStatus.ClosedInPOS:
      return 'Closed in POS';
    case OrderStatus.CloseFailed:
      return 'Close Failed';
    default:
      return 'Unknown';
  }
};

export const getOrderStatusColor = (status: OrderStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
   
    case OrderStatus.New:
      return "info";
    case OrderStatus.Pending:
      return "info";
    case OrderStatus.SentToPOS:
      return "warning"; // Changed from secondary to success
    case OrderStatus.Failed:
      return "error";
    case OrderStatus.CancelledInPOS:
      return "success"; // Or a specific color like grey if available via theme
    case OrderStatus.CancelFailed:
      return "error";
    case OrderStatus.ClosedInPOS:
      return "success"; // Or success
    case OrderStatus.CloseFailed:
      return "error";
    default:
      return "default";
  
  }
};

export const mapCloudStateToOrderStatus = (cloudState: string | undefined): OrderStatus | undefined => {
  if (!cloudState) return undefined;

  switch (cloudState) {
    case CloudOrderState.NEW:
      return OrderStatus.New;
    case CloudOrderState.ACCEPTED:
    // case CloudOrderState.PREORDER_ACCEPTED:
      return OrderStatus.SentToPOS;
    // case CloudOrderState.WAITING_FOR_TRANSPORT:
    // case CloudOrderState.MODIFIED_BY_VENDOR:
    // case CloudOrderState.RECONCILIATION_STARTED:
      return OrderStatus.Pending;
    case CloudOrderState.CLOSED:
    // case CloudOrderState.RECONCILED_WITH_PLATFORM:
    // case CloudOrderState.RECONCILED_WITH_TRANSPORT:
      return OrderStatus.ClosedInPOS;
  case CloudOrderState.CANCELLED:
  case CloudOrderState.CANCELLED_BY_PLATFORM:
  case CloudOrderState.CANCELLED_BY_TRANSPORT:
  case CloudOrderState.CANCELLED_BY_VENDOR:
      return OrderStatus.CancelledInPOS;
    default:
      // If the cloudState is a valid OrderStatus enum key (e.g., "Failed" coming from an older system or custom state)
      // This part is speculative and depends on whether cloudOrderState could ever hold values like "Failed" directly
      // For now, we only map defined CloudOrderState enum values.
      // Consider adding logging for unmapped states if necessary.
      return undefined;
  }
};

export const isOrderActionable = (status: OrderStatus): boolean => {
  return [
    OrderStatus.New,
    OrderStatus.Pending,
    OrderStatus.Failed,
    OrderStatus.CancelFailed,
    OrderStatus.CloseFailed,
  ].includes(status);
};

export const canCancelOrder = (status: OrderStatus): boolean => {
  return [OrderStatus.New, OrderStatus.Pending, OrderStatus.SentToPOS].includes(status);
};

export const canCloseOrder = (status: OrderStatus): boolean => {
  return [OrderStatus.SentToPOS].includes(status);
};

export const canRetryOrder = (status: OrderStatus): boolean => {
  return [OrderStatus.Failed, OrderStatus.CancelFailed, OrderStatus.CloseFailed].includes(status);
};

// String utilities
export const truncateText = (text: string | undefined, maxLength: number = 50): string => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

export const capitalizeFirst = (text: string | undefined): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

// Validation utilities
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^\+?[\d\s-()]+$/;
  return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
};

// File utilities
export const downloadFile = (content: string, filename: string, contentType: string = 'text/plain'): void => {
  const blob = new Blob([content], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Failed to read file as text'));
      }
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsText(file);
  });
};

// Array utilities
export const groupBy = <T>(array: T[], keyFn: (item: T) => string): Record<string, T[]> => {
  return array.reduce((groups, item) => {
    const key = keyFn(item);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {} as Record<string, T[]>);
};

export const sortBy = <T>(array: T[], keyFn: (item: T) => any, direction: 'asc' | 'desc' = 'asc'): T[] => {
  return [...array].sort((a, b) => {
    const aVal = keyFn(a);
    const bVal = keyFn(b);
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

// Local storage utilities
export const saveToLocalStorage = (key: string, value: any): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

export const loadFromLocalStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return defaultValue;
  }
};

export const removeFromLocalStorage = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing from localStorage:', error);
  }
};

// Debounce utility
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Theme utilities
export const getContrastColor = (hexColor: string): string => {
  // Remove # if present
  const color = hexColor.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

// Error handling utilities
export const getErrorMessage = (error: any): string => {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.response?.data?.error) return error.response.data.error;
  return 'An unexpected error occurred';
};

// Cloud order status utilities
export const getCloudStatusText = (cloudState: string | undefined): string => {
  if (!cloudState) return 'N/A';
  
  switch (cloudState) {
    case 'NEW':
      return 'New';
    case 'ACCEPTED':
      return 'Accepted';
    case 'PREORDER_ACCEPTED':
      return 'Pre-Order';
    case 'WAITING_FOR_TRANSPORT':
      return 'Transport';
    case 'MODIFIED_BY_VENDOR':
      return 'Modified';
    case 'RECONCILIATION_STARTED':
      return 'Reconciling';
    case 'CLOSED':
      return 'Closed';
    case 'RECONCILED_WITH_PLATFORM':
      return 'Reconciled';
    case 'RECONCILED_WITH_TRANSPORT':
      return 'Transport OK';
    case 'CANCELLED':
      return 'Cancelled';
    case 'CANCELLED_BY_PLATFORM':
      return 'Platform Cancel';
    case 'CANCELLED_BY_TRANSPORT':
      return 'Transport Cancel';
    case 'CANCELLED_BY_VENDOR':
      return 'Vendor Cancel';
    default:
      return cloudState;
  }
};

export const getCloudStatusColor = (cloudState: string | undefined): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  if (!cloudState) return 'default';
  
  switch (cloudState) {
    case 'NEW':
      return 'info';
    case 'ACCEPTED':
    case 'PREORDER_ACCEPTED':
      return 'primary';
    case 'WAITING_FOR_TRANSPORT':
    case 'MODIFIED_BY_VENDOR':
    case 'RECONCILIATION_STARTED':
      return 'warning';
    case 'CLOSED':
    case 'RECONCILED_WITH_PLATFORM':
    case 'RECONCILED_WITH_TRANSPORT':
      return 'success';
    case 'CANCELLED':
    case 'CANCELLED_BY_PLATFORM':
    case 'CANCELLED_BY_TRANSPORT':
  case 'CANCELLED_BY_VENDOR':
      return 'error';
    default:
      return 'default';
  }
};

// Route utilities
export const getDefaultRouteForUser = (permissions: string[]): string => {
  // Check permissions in priority order and return the first available route
  
  // First check if user can view orders/dashboard (highest priority)
  if (permissions.includes('orders:read')) {
    return '/';
  }
  
  // Then check other major sections
  if (permissions.includes('revenue:read')) {
    return '/revenue';
  }
  
  if (permissions.includes('branches:read')) {
    return '/branches';
  }
  
  if (permissions.includes('mappings:read')) {
    return '/mappings';
  }
  
  if (permissions.includes('reports:read')) {
    return '/reports';
  }
  
  if (permissions.includes('users:manage')) {
    return '/users';
  }
  
  if (permissions.includes('roles:manage')) {
    return '/roles';
  }
  
  // If no specific permissions found, default to a safe route
  // You might want to create a "no access" page or profile page
  return '/profile';
};
